import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  MarkerType,
  useEdgesState,
  useNodesState,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Antenna,
  Brain,
  Database,
  MessageSquare,
  Network,
  Snowflake,
  Sparkles,
  X,
} from "lucide-react";
import {
  api,
  type KnowledgeFlowEdge,
  type KnowledgeFlowNode,
  type KnowledgeFlowStats,
  type KnowledgeFlowWindow,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** v3: warstwowy katalog; czyścimy stare klucze układu. */
const LAYOUT_KEY = "knowledge-flow-layout-v3";
const LEGACY_LAYOUT_KEYS = ["knowledge-flow-layout-v1", "knowledge-flow-layout-v2"];
const UI_BUILD = "layout-v3";

type FilterMode = "all" | "ai" | "source" | "orphan" | "memory";

type FlowNodeData = {
  payload: KnowledgeFlowNode;
  selectedId: string | null;
  dimmed: boolean;
  windowDays: KnowledgeFlowWindow;
};

type FlowEdgeData = {
  payload: KnowledgeFlowEdge;
  windowDays: KnowledgeFlowWindow;
  highlighted: boolean;
  dimmed: boolean;
  showLabel: boolean;
};

const EDGE_KIND_PL: Record<string, string> = {
  rag: "RAG",
  tools: "narzędzia",
  briefing: "briefing",
  library: "szafka",
  prompts: "prompty",
  corpus: "korpus",
  facets: "agregat",
  memory: "pamięć",
  ingest: "ingest",
};

const EDGE_KIND_COLOR: Record<string, string> = {
  rag: "#7c3aed",
  tools: "#0369a1",
  briefing: "#0f766e",
  library: "#4f46e5",
  prompts: "#c026d3",
  corpus: "#a16207",
  facets: "#ea580c",
  memory: "#2563eb",
  ingest: "#64748b",
};

const STATS_SOURCE_PL: Record<string, string> = {
  bart_messages: "wiadomości Barta (user+assistant)",
  discussions: "uruchomione narady Zespołu AI",
  carousel_generation_runs: "runy LLM w studiu karuzel",
  carousel_czasotekst: "runy Czasotekstu",
  bart_notes: "aktualizacje notatek Barta",
  carousel_lessons: "lekcje / retros karuzel",
  action_audit: "wpisy audytu Voice Assistant",
};

function statsValue(stats: KnowledgeFlowStats, w: KnowledgeFlowWindow): number {
  if (w === 7) return stats.d7;
  if (w === 14) return stats.d14;
  return stats.d28;
}

function edgeKindLabel(label: string): string {
  return EDGE_KIND_PL[label] || label;
}

function edgeKindColor(label: string, access: string, highlighted: boolean): string {
  if (highlighted) return access === "partial" ? "#c2410c" : "#0f766e";
  if (access === "partial") return "#fb923c";
  return EDGE_KIND_COLOR[label] || "#64748b";
}

function statsSourceHint(source: string | null | undefined): string {
  if (!source) return "brak osobnego licznika";
  return STATS_SOURCE_PL[source] || source;
}

function loadLayout(): Record<string, { x: number; y: number }> {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, { x: number; y: number }>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveLayout(nodes: Node[]) {
  const next: Record<string, { x: number; y: number }> = {};
  for (const n of nodes) {
    next[n.id] = { x: n.position.x, y: n.position.y };
  }
  localStorage.setItem(LAYOUT_KEY, JSON.stringify(next));
}

function clearLegacyLayouts() {
  for (const key of LEGACY_LAYOUT_KEYS) {
    localStorage.removeItem(key);
  }
}

function catalogPosition(pos: { x: number; y: number } | undefined) {
  return pos || { x: 0, y: 0 };
}

function subtypeIcon(subtype: string) {
  if (subtype === "memory") return MessageSquare;
  if (subtype === "live") return Sparkles;
  if (subtype === "orphan") return Snowflake;
  if (subtype === "channel") return Antenna;
  return Database;
}

function subtypeChrome(subtype: string, orphan: boolean, cold: boolean) {
  if (orphan) {
    return "border-dashed border-amber-500 bg-amber-50 text-amber-950";
  }
  if (subtype === "memory") {
    return "border-blue-500 bg-blue-50 text-blue-950";
  }
  if (subtype === "live") {
    return "border-emerald-500 bg-emerald-50 text-emerald-950";
  }
  if (subtype === "channel") {
    return "border-violet-500 bg-violet-50 text-violet-950";
  }
  if (cold) {
    return "border-sky-400 bg-sky-50 text-slate-800";
  }
  return "border-stone-400 bg-stone-50 text-stone-900";
}

function AiFlowNode({ data }: NodeProps<Node<FlowNodeData>>) {
  const d = data;
  const n = d.payload;
  const active = d.selectedId === n.id;
  const val = statsValue(n.stats, d.windowDays);
  return (
    <div
      className={cn(
        "w-[118px] rounded-sm border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-zinc-100 shadow-sm transition",
        active && "ring-2 ring-amber-400 ring-offset-1",
        d.dimmed && "opacity-20",
      )}
      title={`${n.label}\nAktywność ${d.windowDays}d: ${val}\n${n.description}`}
    >
      <Handle type="target" position={Position.Top} className="!h-1.5 !w-1.5 !bg-zinc-400" />
      <div className="flex items-center gap-1">
        <Brain className="h-3 w-3 shrink-0 text-amber-300" />
        <div className="truncate text-[10px] font-semibold leading-tight">{n.label}</div>
      </div>
      <div className="mt-0.5 font-mono text-[9px] text-zinc-400">
        {d.windowDays}d · {val}
      </div>
      <Handle type="source" position={Position.Bottom} className="!h-1.5 !w-1.5 !bg-zinc-400" />
    </div>
  );
}

function SourceFlowNode({ data }: NodeProps<Node<FlowNodeData>>) {
  const d = data;
  const n = d.payload;
  const active = d.selectedId === n.id;
  const Icon = subtypeIcon(n.subtype);
  const orphan = n.is_orphan || n.subtype === "orphan";
  const val = statsValue(n.stats, d.windowDays);
  const showStat = n.stats_available || n.stats.d28 > 0;
  return (
    <div
      className={cn(
        "w-[112px] rounded border px-1.5 py-1 shadow-sm transition",
        subtypeChrome(n.subtype, orphan, n.is_cold),
        active && "ring-2 ring-brand ring-offset-1",
        d.dimmed && "opacity-20",
      )}
      title={`${n.label} (${n.subtype})${orphan ? " · luka" : ""}${n.is_cold ? " · zimna 28d" : ""}\n${n.description}`}
    >
      <Handle type="target" position={Position.Top} className="!h-1.5 !w-1.5 !bg-slate-400" />
      <div className="flex items-center gap-1">
        <Icon className="h-3 w-3 shrink-0 opacity-80" />
        <div className="truncate text-[10px] font-semibold leading-tight">{n.label}</div>
      </div>
      <div className="mt-0.5 flex items-center gap-1 text-[8px] uppercase tracking-wide opacity-70">
        <span>{orphan ? "luka" : n.subtype}</span>
        {showStat && <span className="font-mono normal-case">· {d.windowDays}d {val}</span>}
      </div>
      <Handle type="source" position={Position.Bottom} className="!h-1.5 !w-1.5 !bg-slate-400" />
    </div>
  );
}

function FlowEdgeView(props: EdgeProps<Edge<FlowEdgeData>>) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
    markerStart,
    data,
    style,
  } = props;
  const d = data!;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const payload = d.payload;
  const hot = statsValue(payload.stats, 7);
  const strokeWidth = d.highlighted ? 2.8 : Math.min(1 + Math.log10(hot + 1) * 1.2, 3.2);
  const stroke = edgeKindColor(payload.label, payload.access, d.highlighted);
  const val = statsValue(payload.stats, d.windowDays);
  const kind = edgeKindLabel(payload.label);
  const dirMark = payload.direction === "bi" ? "↔" : "→";
  const hint = statsSourceHint(payload.stats_source);

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        markerStart={markerStart}
        style={{
          ...style,
          stroke,
          strokeWidth,
          strokeDasharray: payload.access === "partial" ? "5 4" : undefined,
          opacity: d.dimmed ? 0.08 : 0.9,
        }}
      />
      {d.showLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              opacity: d.dimmed ? 0.12 : 1,
            }}
            className="nodrag nopan pointer-events-none absolute max-w-[140px] rounded border border-white/80 bg-white/95 px-1 py-0.5 text-center shadow-sm"
            title={
              payload.stats_available
                ? `Rodzaj: ${kind} (${payload.direction === "bi" ? "dwukierunkowe" : "jednokierunkowe"})\nLiczba ${d.windowDays}d = ${hint}: ${val}\nTo nie jest osobny licznik tej krawędzi, tylko aktywność powiązanego systemu.`
                : `Rodzaj: ${kind}. Brak licznika aktywności.`
            }
          >
            <div className="text-[8px] font-semibold leading-tight" style={{ color: stroke }}>
              {kind} {dirMark}
            </div>
            {payload.stats_available ? (
              <div className="text-[8px] leading-tight text-slate-500">
                {val}× / {d.windowDays}d
              </div>
            ) : (
              <div className="text-[8px] leading-tight text-slate-400">bez licznika</div>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const nodeTypes = { ai: AiFlowNode, source: SourceFlowNode };
const edgeTypes = { flow: FlowEdgeView };

function DetailPanel({
  node,
  edges,
  windowDays,
  onClose,
}: {
  node: KnowledgeFlowNode;
  edges: KnowledgeFlowEdge[];
  windowDays: KnowledgeFlowWindow;
  onClose: () => void;
}) {
  const incident = edges.filter((e) => e.source === node.id || e.target === node.id);
  const matrixEntries = Object.entries(node.access_matrix || {});

  return (
    <aside className="flex h-full w-full flex-col border-l border-border bg-card md:w-80">
      <div className="flex items-start justify-between gap-2 border-b px-4 py-3">
        <div>
          <div className="text-sm font-semibold">{node.label}</div>
          <div className="text-[11px] text-muted-foreground">
            {node.kind === "ai" ? "system AI" : "źródło"} · {node.subtype}
          </div>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Zamknij">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3 text-sm">
        <p className="text-muted-foreground">{node.description}</p>

        {(node.is_orphan || node.subtype === "orphan") && node.recommend_access && (
          <div className="rounded-md border border-dashed border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-950">
            <div className="font-semibold">Rekomendacja dostępu</div>
            <p className="mt-1">{node.recommend_access}</p>
          </div>
        )}

        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Aktywność
          </div>
          <p className="mb-2 text-[11px] text-muted-foreground">
            Licznik: {statsSourceHint(node.stats_source)}. Okno wybierasz chipami 7 / 14 / 28 u góry.
          </p>
          {!node.stats_available && node.stats.d28 === 0 ? (
            <p className="text-xs text-muted-foreground">Brak licznika dla tego węzła.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {([7, 14, 28] as KnowledgeFlowWindow[]).map((w) => (
                <div
                  key={w}
                  className={cn(
                    "rounded-md border px-2 py-2 text-center",
                    windowDays === w && "border-brand bg-brand/5",
                  )}
                >
                  <div className="text-[10px] text-muted-foreground">{w} dni</div>
                  <div className="text-lg font-semibold">{statsValue(node.stats, w)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {matrixEntries.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Dostęp AI
            </div>
            <ul className="space-y-1 text-xs">
              {matrixEntries.map(([ai, access]) => (
                <li key={ai} className="flex items-center justify-between gap-2">
                  <span>{ai}</span>
                  <Badge
                    variant={access === "full" ? "default" : access === "partial" ? "secondary" : "outline"}
                    className="text-[10px]"
                  >
                    {access === "full" ? "pełny" : access === "partial" ? "częściowy" : "brak"}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {node.paths?.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Ścieżki
            </div>
            <ul className="space-y-1 font-mono text-[10px] text-muted-foreground">
              {node.paths.map((p) => (
                <li key={p} className="break-all">
                  {p}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Połączenia ({incident.length})
          </div>
          <p className="mb-2 text-[11px] text-muted-foreground">
            Liczba przy połączeniu to aktywność powiązanego systemu w oknie (nie osobne „użycia tej krawędzi”).
          </p>
          <ul className="space-y-2 text-xs">
            {incident.map((e) => {
              const other = e.source === node.id ? e.target : e.source;
              return (
                <li key={e.id} className="rounded border px-2 py-1.5">
                  <div className="font-medium" style={{ color: EDGE_KIND_COLOR[e.label] || undefined }}>
                    {edgeKindLabel(e.label)} {e.direction === "bi" ? "↔" : e.source === node.id ? "→" : "←"}{" "}
                    {other}
                  </div>
                  <div className="text-muted-foreground">
                    {e.access === "full" ? "pełny" : e.access === "partial" ? "częściowy" : "brak"} ·{" "}
                    {e.stats_available
                      ? `${statsValue(e.stats, windowDays)}× / ${windowDays}d (${statsSourceHint(e.stats_source)})`
                      : "bez licznika"}
                  </div>
                </li>
              );
            })}
            {incident.length === 0 && (
              <li className="text-muted-foreground">Brak krawędzi (luka albo izolowany korpus).</li>
            )}
          </ul>
        </div>

        {node.href && (
          <Button asChild variant="outline" size="sm" className="w-full">
            <Link to={node.href}>Otwórz {node.href}</Link>
          </Button>
        )}
      </div>
    </aside>
  );
}

export default function PrzeplywWiedzy() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["knowledge-flow-graph"],
    queryFn: () => api.knowledgeFlowGraph(),
  });

  const [windowDays, setWindowDays] = useState<KnowledgeFlowWindow>(7);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [alwaysShowLabels, setAlwaysShowLabels] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const selectedNode = useMemo(
    () => data?.nodes.find((n) => n.id === selectedId) || null,
    [data, selectedId],
  );

  const visibleNodeIds = useMemo(() => {
    if (!data) return new Set<string>();
    const ids = new Set<string>();
    for (const n of data.nodes) {
      if (filter === "all") ids.add(n.id);
      else if (filter === "ai" && n.kind === "ai") ids.add(n.id);
      else if (filter === "source" && n.kind === "source") ids.add(n.id);
      else if (filter === "orphan" && (n.is_orphan || n.subtype === "orphan")) ids.add(n.id);
      else if (filter === "memory" && n.subtype === "memory") ids.add(n.id);
    }
    return ids;
  }, [data, filter]);

  const rebuild = useCallback(() => {
    if (!data) return;
    const layout = loadLayout();
    const showAll = filter === "all";
    const allowed = showAll
      ? new Set(data.nodes.map((n) => n.id))
      : new Set(visibleNodeIds);

    if (!showAll && (filter === "ai" || filter === "source" || filter === "memory")) {
      for (const e of data.edges) {
        if (allowed.has(e.source) || allowed.has(e.target)) {
          allowed.add(e.source);
          allowed.add(e.target);
        }
      }
    }

    const incident = new Set<string>();
    if (selectedId) {
      for (const e of data.edges) {
        if (e.source === selectedId || e.target === selectedId) {
          incident.add(e.id);
        }
      }
    }

    const nextNodes: Node[] = data.nodes
      .filter((n) => allowed.has(n.id))
      .map((n) => {
        const pos = layout[n.id] || catalogPosition(n.position);
        const connected = data.edges.some(
          (e) =>
            (e.source === selectedId && e.target === n.id) ||
            (e.target === selectedId && e.source === n.id),
        );
        const dimmed = Boolean(selectedId && selectedId !== n.id && !connected);
        return {
          id: n.id,
          type: n.kind === "ai" ? "ai" : "source",
          position: pos,
          data: {
            payload: n,
            selectedId,
            dimmed,
            windowDays,
          } satisfies FlowNodeData,
        };
      });

    const nextEdges: Edge[] = data.edges
      .filter((e) => allowed.has(e.source) && allowed.has(e.target))
      .map((e) => {
        const highlighted = incident.has(e.id);
        const dimmed = Boolean(selectedId) && !highlighted;
        const bi = e.direction === "bi";
        return {
          id: e.id,
          source: e.source,
          target: e.target,
          type: "flow",
          markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12 },
          markerStart: bi
            ? { type: MarkerType.ArrowClosed, width: 12, height: 12 }
            : undefined,
          data: {
            payload: e,
            windowDays,
            highlighted,
            dimmed,
            showLabel: alwaysShowLabels ? !dimmed : highlighted,
          } satisfies FlowEdgeData,
        };
      });

    setNodes(nextNodes);
    setEdges(nextEdges);
  }, [
    data,
    filter,
    selectedId,
    windowDays,
    visibleNodeIds,
    alwaysShowLabels,
    setNodes,
    setEdges,
  ]);

  useEffect(() => {
    clearLegacyLayouts();
  }, []);

  useEffect(() => {
    if (!data?.layout) return;
    const key = "knowledge-flow-catalog-layout";
    const prev = localStorage.getItem(key);
    if (prev && prev !== data.layout) {
      localStorage.removeItem(LAYOUT_KEY);
    }
    localStorage.setItem(key, data.layout);
  }, [data?.layout]);

  useEffect(() => {
    rebuild();
  }, [rebuild]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setSelectedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onNodeDragStop = useCallback(
    (_: unknown, __node: Node, allNodes: Node[]) => {
      saveLayout(allNodes.length ? allNodes : nodes);
    },
    [nodes],
  );

  const gaps = useMemo(
    () => (data?.nodes || []).filter((n) => n.is_orphan || n.subtype === "orphan"),
    [data],
  );

  return (
    <div className="flex h-[calc(100vh-5rem)] flex-col">
      <PageHeader
        title="Przepływ wiedzy"
        subtitle="Skąd wiedza wpada do AI, oraz co leży odłogiem bez dostępu modeli."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Odśwież
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                clearLegacyLayouts();
                localStorage.removeItem(LAYOUT_KEY);
                rebuild();
              }}
            >
              Reset układu
            </Button>
          </div>
        }
      />

      <div className="mb-2 rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-950">
        <div className="font-semibold">Jak czytać liczby ({UI_BUILD})</div>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          <li>
            Na <strong>czarnym nodzie AI</strong>: ile razy system był aktywny w oknie 7/14/28 (Bart =
            wiadomości, Zespół = narady, Karuzele = runy LLM).
          </li>
          <li>
            Na <strong>linii</strong> po kliknięciu: ten sam licznik aktywności powiązanego systemu +
            rodzaj przepływu (RAG / narzędzia / briefing). To nie jest osobny licznik „tej krawędzi”.
          </li>
          <li>
            Kolory źródeł: szary=corpus, zielony=live, niebieski=pamięć, fiolet=kanał, żółty=luka.
            Etykiety linii: domyślnie dopiero po kliknięciu w node.
          </li>
        </ul>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-lg border bg-muted/30 p-1">
          {(
            [
              ["all", "Wszystkie"],
              ["ai", "AI"],
              ["source", "Źródła"],
              ["memory", "Pamięci"],
              ["orphan", "Luki"],
            ] as [FilterMode, string][]
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs font-medium transition",
                filter === id ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
              {id === "orphan" && data ? ` (${data.meta.gap_count})` : ""}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg border bg-muted/30 p-1">
          {([7, 14, 28] as KnowledgeFlowWindow[]).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWindowDays(w)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs font-medium transition",
                windowDays === w ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {w} dni
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setAlwaysShowLabels((v) => !v)}
          className={cn(
            "rounded-md border px-2.5 py-1 text-xs font-medium transition",
            alwaysShowLabels ? "border-brand bg-brand/5" : "text-muted-foreground",
          )}
        >
          {alwaysShowLabels ? "Etykiety: zawsze" : "Etykiety: po kliknięciu"}
        </button>
        {data && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Network className="h-3.5 w-3.5" />
            {data.meta.ai_count} AI · {data.meta.source_count} źródeł · {data.meta.edge_count}{" "}
            krawędzi · {UI_BUILD}
          </div>
        )}
      </div>

      <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-5 rounded-sm bg-zinc-900" /> AI
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-5 rounded-sm border border-stone-400 bg-stone-50" /> corpus
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-5 rounded-sm border border-emerald-500 bg-emerald-50" /> live
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-5 rounded-sm border border-blue-500 bg-blue-50" /> pamięć
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-5 rounded-sm border border-violet-500 bg-violet-50" /> kanał
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-5 rounded-sm border border-dashed border-amber-500 bg-amber-50" /> luka
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-0.5 w-5" style={{ background: EDGE_KIND_COLOR.rag }} /> RAG
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-0.5 w-5" style={{ background: EDGE_KIND_COLOR.tools }} /> narzędzia
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-0.5 w-5" style={{ background: EDGE_KIND_COLOR.briefing }} /> briefing
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-0.5 w-5 border-t-2 border-dashed border-orange-400" /> partial
        </span>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Ładowanie grafu…</p>}
      {error && (
        <p className="text-sm text-destructive">
          Nie udało się wczytać mapy: {(error as Error).message}
        </p>
      )}

      {data && (
        <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border bg-slate-50">
          <div className="relative min-w-0 flex-1">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={(_, node) => setSelectedId(node.id)}
              onPaneClick={() => setSelectedId(null)}
              onNodeDragStop={onNodeDragStop}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              fitViewOptions={{ padding: 0.15 }}
              minZoom={0.2}
              maxZoom={1.8}
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={20} size={1} color="#cbd5e1" />
              <Controls showInteractive={false} />
              <MiniMap
                pannable
                zoomable
                nodeColor={(n) => {
                  const payload = (n.data as FlowNodeData | undefined)?.payload;
                  if (!payload) return "#94a3b8";
                  if (payload.kind === "ai") return "#18181b";
                  if (payload.is_orphan || payload.subtype === "orphan") return "#f59e0b";
                  if (payload.subtype === "memory") return "#3b82f6";
                  if (payload.subtype === "live") return "#10b981";
                  if (payload.subtype === "channel") return "#8b5cf6";
                  return "#a8a29e";
                }}
              />
            </ReactFlow>
          </div>
          {selectedNode ? (
            <DetailPanel
              node={selectedNode}
              edges={data.edges}
              windowDays={windowDays}
              onClose={() => setSelectedId(null)}
            />
          ) : filter === "orphan" ? (
            <aside className="hidden w-72 overflow-y-auto border-l bg-card p-4 md:block">
              <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                Luki ({gaps.length})
              </div>
              <ul className="space-y-2 text-xs">
                {gaps.map((g) => (
                  <li key={g.id}>
                    <button
                      type="button"
                      className="w-full rounded border border-dashed border-amber-300 bg-amber-50 px-2 py-1.5 text-left hover:border-amber-500"
                      onClick={() => setSelectedId(g.id)}
                    >
                      <div className="font-medium">{g.label}</div>
                      <div className="text-muted-foreground line-clamp-2">{g.recommend_access}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          ) : null}
        </div>
      )}
    </div>
  );
}
